import React, { ReactNode, useMemo } from 'react'
import {
  ApplicationModalsProvider as CoreApplicationModalsProvider,
  ApplicationModalContext,
  useApplicationModals,
  ModalIds as CoreModalIds,
} from '@adc/ui-core'
import AuthenticationModal from './authentication'
import VerificationModal from './verification'
import { AUTH_INTENT_KINDS } from '../../authToggle/constants'
import { useApplicationContext } from '../context'

/**
 * ADC extends core's ModalIds with authentication and verification modals.
 */
const ModalIds = {
  ...CoreModalIds,
  Authentication: 'authentication',
  Verification: 'verification',
} as const

/**
 * ADC's ApplicationModalsProvider wraps core's provider and extends it with
 * authentication and verification modals that depend on ADC-specific components.
 *
 * Core handles: General and Confirmation modals
 * ADC adds: Authentication and Verification modals
 */
const ApplicationModalsProvider = ({ children }: { children: ReactNode }) => {
  const { capabilities } = useApplicationContext()

  const additionalModals = useMemo(
    () => ({
      [ModalIds.Authentication]: ({
        close,
        modalProps,
      }: {
        close: () => void
        modalProps: any
      }) => {
        if (!capabilities.authentication?.enabled) {
          return null
        }
        return <AuthenticationModal close={close} {...modalProps} />
      },
      [ModalIds.Verification]: ({
        close,
        modalProps,
      }: {
        close: () => void
        modalProps: any
      }) => {
        return <VerificationModal close={close} {...modalProps} />
      },
    }),
    [capabilities]
  )

  return (
    <CoreApplicationModalsProvider
      additionalModals={additionalModals}
      capabilities={capabilities}
      modalIntents={AUTH_INTENT_KINDS}
    >
      {children}
    </CoreApplicationModalsProvider>
  )
}

export {
  ApplicationModalContext,
  ApplicationModalsProvider,
  useApplicationModals,
  ModalIds,
}
